import { defineStore } from 'pinia';
import { ref } from 'vue';
import mockHistoricalData from '@/assets/mockHistoricalData.json';
import mockReactorState from '@/assets/mockReactorState.json';

export const useHistorycalStore = defineStore('history', () => {

    const startDate = ref<string>('');
    const endDate = ref<string>('');
    const reactorsState = ref<State[]>([]);
    const error = ref<string | null>(null);
    const historicalData = ref<HistoricalDataResponse | null>(null);
    const selectedMetric = ref<string>('temperature');
    const selectedMetrics = ref<string[]>(['temperature']); // Par défaut, une métrique sélectionnée
    const temperatureMin = ref<number>(15);
    const temperatureMax = ref<number>(35);
    const phMin = ref<number>(3);   // Valeur par défaut
    const phMax = ref<number>(8);

    const fetchState = async () => {
        try {
            // Chargement direct des données mockées depuis le fichier JSON
            reactorsState.value = mockReactorState;
            error.value = null; // Réinitialiser l'erreur en cas de succès
        } catch (err) {
            console.error('Erreur fetchState:', err);
        }
    };

    const fetchHistoricalData = async () => {
        try {
            const raw = mockHistoricalData as HistoricalDataResponse;
            if (!raw.results || raw.results.length === 0) {
                historicalData.value = raw;
                return;
            }

            const mockFirstTime = new Date(raw.results[0]._time).getTime();
            const mockLastTime  = new Date(raw.results[raw.results.length - 1]._time).getTime();
            const mockDurationMs = mockLastTime - mockFirstTime; // ~41 jours

            // Couvrir les 120 derniers jours (cultures jusqu'à 90 jours)
            const COVERAGE_MS = 120 * 24 * 60 * 60 * 1000;
            const now = Date.now();
            const coverageStart = now - COVERAGE_MS;

            // Tuiler le mock pour remplir la fenêtre coverageStart → now
            const tiledResults: HistoricalDataPoint[] = [];
            let blockStart = coverageStart;
            while (blockStart < now) {
                const blockOffsetMs = blockStart - mockFirstTime;
                for (const r of raw.results) {
                    const t = new Date(r._time).getTime() + blockOffsetMs;
                    if (t >= coverageStart && t <= now) {
                        tiledResults.push({ ...r, _time: new Date(t).toISOString() });
                    }
                }
                blockStart += mockDurationMs;
            }

            // Trier par temps croissant
            tiledResults.sort((a, b) => new Date(a._time).getTime() - new Date(b._time).getTime());

            // Filtrer par la plage de dates sélectionnée si définie
            let results = tiledResults;
            if (startDate.value) {
                const start = new Date(startDate.value).getTime();
                results = results.filter(r => new Date(r._time).getTime() >= start);
            }
            if (endDate.value) {
                const end = new Date(endDate.value).getTime();
                results = results.filter(r => new Date(r._time).getTime() <= end);
            }

            historicalData.value = { ...raw, results };
            error.value = null;
        } catch (err) {
            console.error('Erreur fetchHistoricalData:', err);
        }
    };

    return {
        startDate,
        endDate,
        fetchState,
        fetchHistoricalData,
        error,
        historicalData,
        reactorsState,
        selectedMetric,
        selectedMetrics,
        temperatureMax,
        temperatureMin,
        phMax,
        phMin
    }
});
